import React from 'react';

import { getAuthorizeUrl, deauthorizeConnector } from '@lumapps/connectors/api/connectorsApi';
import { ConnectorAuthorizeResponse } from '@lumapps/connectors/types';
import { get as getConstants } from '@lumapps/constants';
import { AppId } from '@lumapps/constants/app';
import { listAvailableConnectors, ListAvailableConnectorsParams } from '@lumapps/integrations/api';
import { Connector } from '@lumapps/integrations/types';
import { GRANT_TYPE } from '@lumapps/marketplace/sdk';
import { sendWebviewMobileMessage } from '@lumapps/mobile-webview/message-emit';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { GLOBAL } from '@lumapps/translations';
import { useFetchWithStatus } from '@lumapps/utils/hooks/useFetchWithStatus';
import { useInterval } from '@lumapps/utils/hooks/useInterval';
import { UseServerList, useServerList } from '@lumapps/utils/hooks/useServerList';
import { BaseLoadingStatus, isLoading } from '@lumapps/utils/types/BaseLoadingStatus';

import { CLOSE_CONNECTION_WINDOW_KEY, CONNECTION_WINDOW_OPTIONS, INTERVAL_CONNECTION_WINDOW_TIMER } from '../constants';
import { DisconnectConfirmDialog } from '../types';
import { UseProviderList, useProviderList } from './useProviderList';

export interface UseConnectorList
    extends Pick<
            UseServerList<Connector, ListAvailableConnectorsParams>,
            'hasMoreResults' | 'fetchMore' | 'isLoadingMore'
        >,
        Pick<UseProviderList, 'getProvider'> {
    /** List of connectors */
    connectors?: Connector[];
    /** State of the disconnect confirmation dialog */
    disconnectConfirmDialog: DisconnectConfirmDialog;
    /** Indicates whether a connector is currently connecting */
    isConnecting: boolean;
    /** Indicates whether a connector is currently disconnecting */
    isDisconnecting: boolean;
    /** whether the connectors/providers are loading or not */
    isLoading: boolean;
    /** Callback function used when disconnection is confirmed */
    confirmDisconnectDialogCallback: (confirmed: boolean) => void;
    /** Callback function used to handle disconnection of a connector */
    handleConnectorDisconnect: (connector: Connector) => void;
    /** Callback function used to handle the connection of a connector */
    handleConnectorConnect: (connector: Connector) => void;
    /** Function to set the state of the disconnect confirmation dialog */
    setDisconnectConfirmDialog: React.Dispatch<React.SetStateAction<DisconnectConfirmDialog>>;
}

const { applicationId } = getConstants();
const isWebviewApp = applicationId === AppId.webview;

/**
 * Hook to handle the display of the list of connectors.
 * @param {Boolean} initFetchConnectors - Wether to retrieve the list of connectors at init.
 * @param {Function} closeAuthWindowCallback - Callback to be executed when the `authorizationWindow` is closed
 */
export const useConnectorList = ({
    initFetchConnectors = true,
    closeAuthWindowCallback = undefined,
}: {
    initFetchConnectors?: boolean;
    closeAuthWindowCallback?: () => void;
} = {}): UseConnectorList => {
    const { error } = useNotification();
    const [authorizationWindow, setAuthorizationWindow] = React.useState<Window | null>(null);
    const [disconnectConfirmDialog, setDisconnectConfirmDialog] = React.useState<DisconnectConfirmDialog>({
        isOpen: false,
    });

    const { status: providerListStatus, getProvider } = useProviderList();

    /**
     * Fetches the list of connectors.
     */
    const connectorList = useServerList<Connector, ListAvailableConnectorsParams>({
        params: { grantType: GRANT_TYPE.authorization_code },
        onFetch: listAvailableConnectors,
    });

    /**
     * Retrieve the list of connectors at init.
     */
    React.useEffect(() => {
        if (connectorList.status === BaseLoadingStatus.initial && initFetchConnectors) {
            connectorList.fetch();
        }
    }, [connectorList, initFetchConnectors]);

    /**
     * Fetches the authorization URL for a connector.
     */
    const authorizeUrl = useFetchWithStatus<ConnectorAuthorizeResponse>({
        onFetch: getAuthorizeUrl,
    });

    /**
     * Click handler for connecting a connector.
     *
     * @param {Connector} connector - The connector to connect.
     */
    const handleConnectorConnect = (connector: Connector) => {
        authorizeUrl.fetch({
            params: connector.id,
            callback: ({ success, response }) => {
                const url = response?.link;
                if (!success || !url) {
                    error({ translate: GLOBAL.GENERIC_ERROR });
                    return;
                }

                if (isWebviewApp) {
                    sendWebviewMobileMessage({
                        type: 'redirect',
                        payload: { type: 'connector-card', connectorId: connector.id, url },
                    });
                    return;
                }

                // Open auth window and keep a reference to it
                const authWindow = window.open(url, '_blank', CONNECTION_WINDOW_OPTIONS);
                setAuthorizationWindow(authWindow);
            },
        });
    };

    /**
     * Callback function to close the authorization window.
     */
    const handleCloseAuthorizationWindow = React.useCallback(() => {
        authorizeUrl.reset();
        setAuthorizationWindow(null);
        // Refetch connector list after a connection
        if (initFetchConnectors) {
            connectorList.fetch();
        }
        if (closeAuthWindowCallback) {
            closeAuthWindowCallback();
        }
    }, [authorizeUrl, connectorList, initFetchConnectors, closeAuthWindowCallback]);

    /**
     * Monitors the authorization window and closes it if necessary.
     *
     * @return {undefined}
     */
    const monitorAuthorizationWindow = () => {
        if (authorizationWindow) {
            if (authorizationWindow.closed) {
                handleCloseAuthorizationWindow();
            } else {
                try {
                    // Check if the URL of the window contains the key and if so, close it.
                    const url = authorizationWindow.location.href;

                    if (url.includes(CLOSE_CONNECTION_WINDOW_KEY)) {
                        authorizationWindow.close();
                        handleCloseAuthorizationWindow();
                    }
                } catch (exception) {
                    return undefined;
                }
            }
        }

        return undefined;
    };

    /**
     * Sets up an interval to continuously monitor the authorization window.
     */
    useInterval(
        monitorAuthorizationWindow,
        !isLoading(authorizeUrl.status) && authorizeUrl.response?.link ? INTERVAL_CONNECTION_WINDOW_TIMER : null,
    );

    /**
     * To deauthorize a connector.
     */
    const deauthorize = useFetchWithStatus<ConnectorAuthorizeResponse>({
        onFetch: deauthorizeConnector,
    });

    /**
     * Disconnect click handler.
     *
     * @param {Connector} connector
     */
    const handleConnectorDisconnect = (connector: Connector) => {
        setDisconnectConfirmDialog({ connector, isOpen: true });
    };

    /**
     * Confirm diconnection dialog callback.
     *
     * @param {boolean} confirmed
     */
    const confirmDisconnectDialogCallback = React.useCallback(
        (confirmed: boolean) => {
            if (confirmed && disconnectConfirmDialog.connector) {
                // Disconnect from connector call
                deauthorize.fetch({ params: disconnectConfirmDialog.connector.id });
            }
            setDisconnectConfirmDialog({ isOpen: false });
        },
        [deauthorize, disconnectConfirmDialog.connector],
    );

    /**
     * Refetch connector list after a deauthorize
     */
    React.useEffect(() => {
        if (deauthorize.status === BaseLoadingStatus.idle && !isLoading(connectorList.status)) {
            connectorList.fetch();
            deauthorize.reset();
        }
    }, [connectorList, deauthorize]);

    return {
        connectors: connectorList.items,
        disconnectConfirmDialog,
        hasMoreResults: connectorList.hasMoreResults,
        isConnecting: authorizeUrl.status === BaseLoadingStatus.loading || !!authorizationWindow,
        isDisconnecting: deauthorize.status === BaseLoadingStatus.loading,
        isLoading: isLoading(providerListStatus) || isLoading(connectorList.status),
        isLoadingMore: connectorList.isLoadingMore,
        confirmDisconnectDialogCallback,
        fetchMore: connectorList.fetchMore,
        getProvider,
        handleConnectorConnect,
        handleConnectorDisconnect,
        setDisconnectConfirmDialog,
    };
};
